import type { PropertyContact, PropertyContactList } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PropertyContactClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const propertyContactsQueryKey = (propertyId: number) => [
  queryKeys.properties.root,
  'contacts',
  propertyId,
];

export const usePropertyContactsQuery = <TSelectData = PropertyContactList,>({
  propertyId,
  enabled = true,
  select,
}: {
  propertyId: number;
  enabled?: boolean;
  select?: (data?: PropertyContactList) => TSelectData;
}) => {
  return useQuery({
    queryFn: () => PropertyContactClientService.listForProperty(propertyId),
    queryKey: propertyContactsQueryKey(propertyId),
    enabled: enabled && !!propertyId,
    select,
  });
};

// Mutations invalidate the whole properties root so the contacts list refreshes and the
// property record picks up any billing-contact sync the backend wrote to property_* fields.
const invalidateProperties = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: [queryKeys.properties.root] });

export const usePropertyContactCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: PropertyContact) => {
      return await PropertyContactClientService.Create(req);
    },
    onSuccess: async () => {
      await invalidateProperties(queryClient);
    },
  });
};

export const usePropertyContactUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: PropertyContact) => {
      return await PropertyContactClientService.Update(req);
    },
    onSuccess: async () => {
      await invalidateProperties(queryClient);
    },
  });
};

export const usePropertyContactDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: PropertyContact) => {
      return await PropertyContactClientService.Delete(req);
    },
    onSuccess: async () => {
      await invalidateProperties(queryClient);
    },
  });
};
